import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';

export interface MentionUser {
  userId: string;
  email: string;
  name: string;
}

export interface MentionCandidate {
  _id: string;
  email: string;
  fullname: string;
}

@Component({
  selector: 'app-mention-editor',
  templateUrl: './mention-editor.component.html',
  styleUrls: ['./mention-editor.component.css'],
  standalone: false
})
export class MentionEditorComponent implements AfterViewInit, OnChanges {
  @Input() value = '';
  @Input() taggedUsers: object[] = [];
  @Input() users: object[] = [];
  @Input() placeholder = 'Type @ to tag and notify another user';
  @Input() minRows = 5;
  @Output() valueChange = new EventEmitter<string>();
  @Output() taggedUsersChange = new EventEmitter<object[]>();
  @ViewChild('editor') editor!: ElementRef<HTMLDivElement>;

  suggestions: MentionCandidate[] = [];
  activeSuggestion = 0;
  suggestionTop = 0;
  suggestionLeft = 0;
  private queryRange: Range | null = null;

  ngAfterViewInit(): void {
    this.editor.nativeElement.textContent = this.value || '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.editor && changes['value'] && this.value !== this.readValue()) {
      // External resets (for example, opening another comment) replace the draft.
      this.editor.nativeElement.textContent = this.value || '';
      this.closeSuggestions();
    }
  }

  onInput(): void {
    this.publish();
    this.updateSuggestions();
  }

  onBlur(): void {
    this.closeSuggestions();
  }

  onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('mention-chip-remove')) {
      if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        const chip = target.closest<HTMLElement>('.mention-chip');
        if (chip) this.removeChip(chip);
        this.editor.nativeElement.focus();
      }
      return;
    }
    if (this.suggestions.length) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeSuggestion = (this.activeSuggestion + (event.key === 'ArrowDown' ? 1 : -1) + this.suggestions.length) % this.suggestions.length;
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        this.selectSuggestion(this.suggestions[this.activeSuggestion]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeSuggestions();
        return;
      }
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.insertPlainText('\n');
      this.onInput();
      return;
    }

    if (event.key === 'Backspace' || event.key === 'Delete') {
      const chip = this.adjacentChip(event.key === 'Backspace');
      if (chip) {
        event.preventDefault();
        this.removeChip(chip);
      }
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') || '';
    this.insertPlainText(text);
    this.onInput();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.insertPlainText(event.dataTransfer?.getData('text/plain') || '');
    this.onInput();
  }

  selectSuggestion(user: MentionCandidate): void {
    if (!this.queryRange || !this.editor) return;
    const range = this.queryRange;
    range.deleteContents();
    const chip = document.createElement('span');
    chip.className = 'mention-chip';
    chip.contentEditable = 'false';
    chip.dataset['userId'] = user._id;
    chip.dataset['email'] = user.email;
    chip.dataset['name'] = user.fullname;
    chip.setAttribute('aria-label', `Mention ${user.fullname}`);
    const name = document.createElement('span');
    name.textContent = user.fullname;
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'mention-chip-remove';
    remove.setAttribute('aria-label', `Remove mention ${user.fullname}`);
    remove.textContent = '×';
    chip.append(name, remove);
    range.insertNode(chip);
    const trailing = document.createTextNode('\u00a0');
    chip.after(trailing);
    range.setStart(trailing, 1);
    range.collapse(true);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    this.closeSuggestions();
    this.publish();
    this.editor.nativeElement.focus();
  }

  onEditorClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const button = target.closest('.mention-chip-remove');
    if (button && this.editor.nativeElement.contains(button)) {
      event.preventDefault();
      const chip = button.closest<HTMLElement>('.mention-chip');
      if (chip) this.removeChip(chip);
      this.editor.nativeElement.focus();
    }
  }

  private updateSuggestions(): void {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !this.editor.nativeElement.contains(selection.anchorNode)) {
      this.closeSuggestions();
      return;
    }
    const node = selection.anchorNode;
    if (node?.nodeType !== Node.TEXT_NODE) {
      this.closeSuggestions();
      return;
    }
    const before = (node.textContent || '').slice(0, selection.anchorOffset);
    const match = /(?:^|\s)@([^@\n]*)$/.exec(before);
    if (!match) {
      this.closeSuggestions();
      return;
    }
    const term = match[1].toLowerCase();
    this.suggestions = this.users
      .filter((user): user is MentionCandidate => this.isCandidate(user))
      .filter(user => user.fullname.toLowerCase().startsWith(term)).slice(0, 8);
    this.activeSuggestion = 0;
    if (!this.suggestions.length) {
      this.closeSuggestions();
      return;
    }
    this.queryRange = document.createRange();
    this.queryRange.setStart(node, selection.anchorOffset - match[1].length - 1);
    this.queryRange.setEnd(node, selection.anchorOffset);
    this.positionSuggestions();
  }

  positionSuggestions(): void {
    if (!this.queryRange || !this.editor) return;
    const wrap = this.editor.nativeElement.parentElement;
    if (!wrap) return;
    const caretRange = this.queryRange.cloneRange();
    caretRange.collapse(false);
    const caretRect = caretRange.getBoundingClientRect();
    const anchorRect = caretRect.height ? caretRect : this.queryRange.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    const menuWidth = Math.min(288, wrapRect.width);
    this.suggestionTop = anchorRect.bottom - wrapRect.top + 2;
    this.suggestionLeft = Math.max(0, Math.min(anchorRect.left - wrapRect.left, wrapRect.width - menuWidth));
  }

  private adjacentChip(backwards: boolean): HTMLElement | null {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !selection.isCollapsed) return null;
    const node = selection.anchorNode;
    const offset = selection.anchorOffset;
    if (!node || !this.editor.nativeElement.contains(node)) return null;
    let neighbor: Node | null = null;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (backwards && offset === 0) neighbor = node.previousSibling;
      if (!backwards && offset === text.length) neighbor = node.nextSibling;
      if (backwards && offset === 1 && text[0] === '\u00a0') neighbor = node.previousSibling;
    } else {
      neighbor = node.childNodes[offset + (backwards ? -1 : 0)] || null;
    }
    return neighbor instanceof HTMLElement && neighbor.classList.contains('mention-chip') ? neighbor : null;
  }

  private publish(): void {
    const value = this.readValue();
    const users: MentionUser[] = Array.from(this.editor.nativeElement.querySelectorAll<HTMLElement>('.mention-chip'))
      .map(chip => ({ userId: chip.dataset['userId'] || '', email: chip.dataset['email'] || '', name: chip.dataset['name'] || '' }))
      .filter((user, index, all) => user.userId && all.findIndex(other => other.userId === user.userId) === index);
    this.value = value;
    this.taggedUsers = users;
    this.valueChange.emit(value);
    this.taggedUsersChange.emit(users);
  }

  private removeChip(chip: HTMLElement): void {
    // The separator is inserted with the chip and must disappear with it.
    const next = chip.nextSibling;
    if (next?.nodeType === Node.TEXT_NODE && next.textContent?.startsWith('\u00a0')) {
      next.textContent = next.textContent.slice(1);
    }
    chip.remove();
    this.publish();
  }

  private insertPlainText(text: string): void {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!this.editor.nativeElement.contains(range.commonAncestorContainer)) return;
    range.deleteContents();
    const node = document.createTextNode(text);
    range.insertNode(node);
    range.setStart(node, text.length);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  private readValue(): string {
    if (!this.editor) return this.value || '';
    const read = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
      if (!(node instanceof HTMLElement)) return '';
      if (node.classList.contains('mention-chip')) return `@${node.dataset['name'] || ''}`;
      if (node.tagName === 'BR') return '\n';
      const children = Array.from(node.childNodes).map(read).join('');
      return node !== this.editor.nativeElement && (node.tagName === 'DIV' || node.tagName === 'P') ? `${children}\n` : children;
    };
    return read(this.editor.nativeElement).replace(/\u00a0/g, ' ');
  }

  private closeSuggestions(): void {
    this.suggestions = [];
    this.queryRange = null;
  }

  private isCandidate(user: object): user is MentionCandidate {
    const candidate = user as Partial<MentionCandidate>;
    return typeof candidate._id === 'string' && typeof candidate.fullname === 'string' && typeof candidate.email === 'string';
  }
}
